import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuth: boolean;
  userId: string;
  userName: string;
  userRole: string;
  userTeamId: string;
  userTeamName: string;
  userPosition: string;
  login: (token: string, user: any) => void;
  logout: () => void;
  resetTimer: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const SESSION_TIMEOUT = 20 * 60 * 1000; // 20분

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuth: !!sessionStorage.getItem('access_token'),
    userId: '',
    userName: '',
    userRole: '',
    userTeamId: '',
    userTeamName: '',
    userPosition: '',
  });

  const updateAuthState = (user: any) => {
    setAuthState({
      isAuth: !!sessionStorage.getItem('access_token'),
      userId: user.id || '',
      userName: user.userName || '',
      userRole: user.role || '',
      userTeamId: user.teamId || '',
      userTeamName: user.teamName || '',
      userPosition: user.position || '',
    });
  };

  const resetTimer = () => {
    const expireAt = Date.now() + SESSION_TIMEOUT;
    sessionStorage.setItem('session_expire_at', expireAt.toString());
  };

  useEffect(() => {
    const userStr = sessionStorage.getItem('user');
    const expireAtStr = sessionStorage.getItem('session_expire_at');
    
    if (userStr && expireAtStr) {
      const expireAt = parseInt(expireAtStr, 10);
      if (Date.now() > expireAt) {
        logout(); // 이미 만료된 경우 로그아웃
        return;
      }

      try {
        const user = JSON.parse(userStr);
        updateAuthState(user);
      } catch (e) {
        console.error('Failed to parse user info', e);
      }
    }
  }, []);

  const login = (token: string, user: any) => {
    sessionStorage.setItem('access_token', token);
    sessionStorage.setItem('user', JSON.stringify(user));
    resetTimer();
    updateAuthState(user);
  };

  const logout = () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('session_expire_at');
    setAuthState({
      isAuth: false,
      userId: '',
      userName: '',
      userRole: '',
      userTeamId: '',
      userTeamName: '',
      userPosition: '',
    });
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, resetTimer }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
